// OWNER: FRONTEND-DIALER agent. SPEC §15.2: search (name / phone / city), filter by campaign, status and state, business
// drawer with history, edit fields, add to DNC (confirmed), open in the dialer (/api/queue/item/:membershipId).
// Migration 0006: the queue never serves leads in hand-pick-only states (Oklahoma); setters filter by state here and
// press Call on each one (the Dialer links here as /leads?campaignId=&state=US-OK).
// Endpoint shapes: CORE's contract in src/shared/api-core.ts (Page<BusinessListItem>, BusinessDetail).
import '../dialer/dialer.css';
import { useEffect, useState } from 'preact/hooks';
import type { Business, MembershipStatus, StateRule } from '../../../src/shared/api';
import { ApiFailure, errorMessage } from '../api';
import { navigate } from '../app';
import { Empty, ErrorBanner, fmtDateTime, timeAgo, toast } from '../components/ui';
import { ConfirmButton, Drawer, Field, Spinner, useAsync, useDebounced } from '../components/ui-extra';
import { endpoints } from '../dialer/endpoints';
import { DISPOSITION_META, fmtPhone, lineTypeLabel, MEMBERSHIP_STATUS_LABELS, membershipTone } from '../dialer/labels';
import { callableMemberships, canOpenLead, handPickRule, leadsFiltersFromSearch, stateFilterOptions } from '../dialer/leads';
import { LeadLinks, PhoneKindNote } from '../dialer/LeadLinks';
import { pickNiche } from '../dialer/links';
import { openMembership } from '../dialer/store';
import { fmtDuration } from '../dialer/time';
import type { BusinessDetail, BusinessListItem, BusinessPatch } from '../dialer/types';

const PAGE = 50;

const EDITABLE: Array<{ key: keyof BusinessPatch; label: string; type?: string; hint?: string }> = [
  { key: 'dmName', label: 'Decision-maker name' },
  { key: 'dmRole', label: 'Decision-maker role' },
  { key: 'dmSourceUrl', label: 'Where the DM name came from (URL)', type: 'url' },
  { key: 'gatekeeperName', label: 'Gatekeeper name' },
  { key: 'bestTimeNote', label: 'Best time to reach the DM' },
  { key: 'phoneExt', label: 'Extension' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'emailSourceUrl', label: 'Where the email is published (URL)', type: 'url', hint: 'For the record. Canada: we email only an address the owner gives on the call (CASL), never one taken from a website.' },
  { key: 'website', label: 'Website', type: 'url' },
  { key: 'gbpUrl', label: 'Google Business Profile URL', type: 'url' },
  { key: 'city', label: 'City' },
];

function flags(b: Business): string[] {
  const f: string[] = [];
  if (b.doNotContact) f.push('Do not contact');
  if (b.badNumber) f.push('Bad number');
  if (b.isHealthcare && b.country === 'CA') f.push('CA healthcare: no calls');
  if (b.nationalDnc) f.push('National DNC');
  return f;
}

function LeadDrawer(props: { businessId: number; onClose: () => void; onChanged: (b: Business) => void }) {
  const detail = useAsync<BusinessDetail>(() => endpoints.business(props.businessId), [props.businessId]);
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dncReason, setDncReason] = useState('');
  const [opening, setOpening] = useState<number | null>(null);

  const b = detail.data?.business;
  useEffect(() => {
    if (!b) return;
    const f: Record<string, string> = {};
    for (const e of EDITABLE) f[e.key] = (b[e.key] as string | null) ?? '';
    setForm(f);
  }, [b?.id]);

  const patch: BusinessPatch = {};
  if (b) for (const e of EDITABLE) {
    const v = (form[e.key] ?? '').trim();
    if (v !== ((b[e.key] as string | null) ?? '')) (patch as Record<string, string | null>)[e.key] = v || null;
  }
  const dirty = Object.keys(patch).length > 0;
  const caslWarn = b?.country === 'CA' && (form.email ?? '').trim() && !(form.emailSourceUrl ?? '').trim();

  async function save() {
    if (!b || !dirty) return;
    for (const k of ['dmSourceUrl', 'emailSourceUrl', 'website', 'gbpUrl'] as const) {
      const v = patch[k];
      if (v && !/^https?:\/\//i.test(v)) { setSaveError(`${EDITABLE.find((e) => e.key === k)!.label} must start with http:// or https://`); return; }
    }
    if (patch.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patch.email)) { setSaveError("That email doesn't look right."); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await endpoints.updateBusiness(b.id, patch);
      const next = updated && typeof updated === 'object' && 'id' in updated ? updated : { ...b, ...patch } as Business;
      detail.setData({ ...detail.data!, business: next });
      props.onChanged(next);
      toast('Saved.', 'ok');
    } catch (e) {
      setSaveError(errorMessage(e));
    } finally { setSaving(false); }
  }

  async function addDnc() {
    if (!b) return;
    try {
      await endpoints.addBusinessToDnc(b.id, dncReason.trim() || 'Added from Leads');
      toast(`${b.name} is on the do-not-call list for every campaign.`, 'ok');
      detail.reload();
      props.onChanged({ ...b, doNotContact: true });
    } catch (e) {
      toast(`Couldn't add to do-not-call: ${errorMessage(e)}`, 'bad');
    }
  }

  async function open(membershipId: number) {
    setOpening(membershipId);
    try {
      if ((await openMembership(membershipId)) !== 'failed') navigate('/');
    } finally { setOpening(null); }
  }

  return (
    <Drawer title={b?.name ?? 'Lead'} onClose={props.onClose} width={680}>
      <ErrorBanner error={detail.error ? `Couldn't load this business: ${detail.error}` : null} />
      {!b ? (detail.loading ? <p class="muted"><Spinner /> Loading…</p> : null) : (
        <div class="stack" style="gap:14px">
          <div>
            <div class="mono" style="font-size:16px">{fmtPhone(b.phoneE164)}{b.phoneExt ? ` ext. ${b.phoneExt}` : ''}</div>
            <div class="muted">{[b.city, b.stateProv, b.country].filter(Boolean).join(', ')} · {b.tz} · {lineTypeLabel(b.lineType)}{b.niche ? ` · ${b.niche}` : ''}</div>
            <PhoneKindNote business={b} />
            <div class="row" style="gap:6px;flex-wrap:wrap;margin-top:6px">
              {flags(b).map((f) => <span class="chip chip-bad">{f}</span>)}
              {/* PJ 2026-10-09: the niche of the newest campaign it is in, else the business's own. */}
              <LeadLinks business={b} niche={pickNiche(detail.data!.memberships[0]?.campaignNiche, b.niche)} />
            </div>
          </div>

          <div>
            <div class="eyebrow" style="margin-bottom:4px">Campaigns</div>
            {detail.data!.memberships.length === 0 ? <p class="muted" style="margin:0">Not in any campaign.</p> : (
              <div class="vd-table-wrap">
                <table>
                  <thead><tr><th>Campaign</th><th>Status</th><th>Attempts</th><th>VMs</th><th>Last</th><th></th></tr></thead>
                  <tbody>
                    {detail.data!.memberships.map((m) => {
                      // PJ 2026-10-28: a meeting booked opens (Dial asks "Meeting booked. Call anyway?" first).
                      const closed = !canOpenLead(m.status) || b.doNotContact;
                      return (
                        <tr>
                          <td>{m.campaignName}</td>
                          <td><span class={`chip chip-${membershipTone(m.status) || 'x'}`}>{MEMBERSHIP_STATUS_LABELS[m.status] ?? m.status}</span></td>
                          <td>{m.attempts}</td>
                          <td>{m.vmCount}</td>
                          <td>{m.lastAttemptAt ? timeAgo(m.lastAttemptAt) : '—'}</td>
                          <td style="text-align:right">
                            <button class="btn btn-sm btn-primary" disabled={closed || opening !== null}
                              title={closed ? 'Closed or do-not-contact; it cannot be dialed.'
                                : m.status === 'meeting' ? 'Meeting booked: open it on the dial screen; Dial asks "Meeting booked. Call anyway?" before it calls'
                                  : 'Open it on the dial screen (it will not dial until you press Space)'}
                              onClick={() => void open(m.id)}>{opening === m.id ? 'Opening…' : 'Open in dialer'}</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <div class="eyebrow" style="margin-bottom:4px">Details</div>
            <div class="vd-edit-grid">
              {EDITABLE.map((e) => (
                <Field label={e.label} hint={e.key === 'emailSourceUrl' && caslWarn ? 'Canada: add the page where this email is published, or it cannot be emailed.' : e.hint}>
                  <input type={e.type ?? 'text'} value={form[e.key] ?? ''} onInput={(ev) => setForm({ ...form, [e.key]: ev.currentTarget.value })} />
                </Field>
              ))}
            </div>
            <ErrorBanner error={saveError} />
            <div class="row" style="gap:8px;margin-top:8px">
              <button class="btn btn-primary" disabled={!dirty || saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save changes'}</button>
              {dirty ? <button class="btn btn-sm" onClick={() => { const f: Record<string, string> = {}; for (const e of EDITABLE) f[e.key] = (b[e.key] as string | null) ?? ''; setForm(f); }}>Undo</button> : null}
            </div>
          </div>

          {detail.data!.openTasks.length ? (
            <div>
              <div class="eyebrow">Open tasks</div>
              <ul class="vd-list">{detail.data!.openTasks.map((t) => <li><b>{t.title}</b> <span class="muted">· due {fmtDateTime(t.dueAt)}</span></li>)}</ul>
            </div>
          ) : null}

          <div>
            <div class="eyebrow">History</div>
            {detail.data!.history.length === 0 ? <p class="muted" style="margin:4px 0">No calls yet.</p> : (
              <ul class="vd-list vd-history">
                {detail.data!.history.map((h) => (
                  <li>
                    <span class="mono">{fmtDateTime(h.createdAt)}</span>{' '}
                    <b>{h.disposition ? DISPOSITION_META[h.disposition]?.label ?? h.disposition : h.direction === 'inbound' ? 'Inbound call' : 'No outcome logged'}</b>
                    <span class="muted">{h.durationSec ? ` · ${fmtDuration(h.durationSec)}` : ''}{h.userName ? ` · ${h.userName}` : ''}{h.campaignName ? ` · ${h.campaignName}` : ''}</span>
                    {h.note ? <div class="vd-note">{h.note}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {detail.data!.emails?.length ? (
            <div>
              <div class="eyebrow">Emails sent</div>
              <ul class="vd-list vd-history">
                {detail.data!.emails.map((m) => (
                  <li>
                    <span class="mono">{m.sentAt ? fmtDateTime(m.sentAt) : '—'}</span>{' '}
                    <b>{m.status === 'sent' ? m.subject : `Not sent: ${m.subject ?? ''}`}</b>
                    <span class="muted"> · {m.typeLabel} · to {m.to}{m.sentBy ? ` · by ${m.sentBy}` : ''}{m.fromEmail ? ` (${m.fromEmail})` : ''}{m.messageId ? ` · id ${m.messageId}` : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {detail.data!.meetings?.length ? (
            <div>
              <div class="eyebrow">Meetings</div>
              <ul class="vd-list">{detail.data!.meetings.map((m) => <li><b>{fmtDateTime(m.scheduledFor)}</b> <span class="muted">· {m.status.replace('_', '-')} · set by {m.setByName}</span></li>)}</ul>
            </div>
          ) : null}

          {!b.doNotContact && !detail.data!.onInternalDnc ? (
            <div class="card" style="border-color:var(--red-tint)">
              <div class="eyebrow">Do not call</div>
              <p style="margin:4px 0 8px;font-size:14px">Adds this number to the internal do-not-call list for every campaign, now and in future imports. There is no undo in the app.</p>
              <div class="row" style="gap:8px;flex-wrap:wrap">
                <input style="flex:1;min-width:200px" placeholder="Reason (e.g. asked by email not to be called)" value={dncReason} onInput={(e) => setDncReason(e.currentTarget.value)} />
                <ConfirmButton label="Add to do-not-call" confirmLabel="Yes, never call them" onConfirm={addDnc} />
              </div>
            </div>
          ) : <p class="chip chip-bad" style="display:block;padding:8px 10px">{detail.data!.onInternalDnc ? 'This number is on the internal do-not-call list.' : 'This business is marked do-not-contact.'} It will not be dialed.</p>}
        </div>
      )}
    </Drawer>
  );
}

export function Leads(_props: { params: Record<string, string> }) {
  const [initial] = useState(() => leadsFiltersFromSearch(location.search));
  const [q, setQ] = useState('');
  const [campaignId, setCampaignId] = useState<number | null>(initial.campaignId);
  const [status, setStatus] = useState<MembershipStatus | ''>('');
  const [stateCode, setStateCode] = useState(initial.state);
  const [calling, setCalling] = useState<number | null>(null);
  const [rows, setRows] = useState<BusinessListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const campaigns = useAsync(() => endpoints.campaigns(), []);
  const stateRules = useAsync<StateRule[]>(() => endpoints.stateRules(), []);
  const dq = useDebounced(q.trim(), 300);

  const load = async (offset: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await endpoints.searchLeads({ q: dq || undefined, campaignId, status, state: stateCode || undefined, limit: PAGE, offset });
      const items = Array.isArray(res?.items) ? res.items : [];
      setRows((prev) => (offset === 0 ? items : [...prev, ...items]));
      setTotal(typeof res?.total === 'number' ? res.total : items.length);
    } catch (e) {
      setError(e instanceof ApiFailure && e.status === 404 ? 'Lead search is not available on the server yet.' : errorMessage(e));
      if (offset === 0) setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(0); }, [dq, campaignId, status, stateCode]);
  // The Dialer's link carries the starting filters; drop them from the address once read so they don't go stale.
  useEffect(() => { if (location.search) history.replaceState(null, '', location.pathname); }, []);

  const onChanged = (b: Business) => setRows((rs) => rs.map((r) => (r.business.id === b.id ? { ...r, business: b } : r)));

  /** Row "Call": opens that lead on the dial screen (never dials it; Space does). Several open campaigns: the drawer. */
  async function callRow(businessId: number, membershipIds: number[]) {
    if (membershipIds.length !== 1) { setOpenId(businessId); return; }
    setCalling(membershipIds[0]);
    try {
      if ((await openMembership(membershipIds[0])) !== 'failed') navigate('/');
    } finally { setCalling(null); }
  }
  const stateGroups = stateFilterOptions(stateRules.data);
  const pickedRule = stateCode ? (stateRules.data ?? []).find((r) => r.code === stateCode) ?? null : null;

  return (
    <div style="max-width:1200px">
      <div class="vd-page-head">
        <div>
          <h1 style="margin:0">Leads</h1>
          <p class="muted" style="margin:4px 0 0">Every business in your campaigns. Opening one in the dialer never dials it.</p>
        </div>
      </div>
      <div class="vd-filters">
        <input type="search" placeholder="Search name, phone or city" value={q} onInput={(e) => setQ(e.currentTarget.value)} style="min-width:260px;flex:1;max-width:420px" aria-label="Search leads" />
        <select aria-label="Campaign" value={campaignId ?? ''} onChange={(e) => setCampaignId(e.currentTarget.value ? Number(e.currentTarget.value) : null)}>
          <option value="">All campaigns</option>
          {(campaigns.data ?? []).map((c) => <option value={c.id}>{c.name}</option>)}
        </select>
        <select aria-label="Status" value={status} onChange={(e) => setStatus(e.currentTarget.value as MembershipStatus | '')}>
          <option value="">Any status</option>
          {(Object.keys(MEMBERSHIP_STATUS_LABELS) as MembershipStatus[]).map((s) => <option value={s}>{MEMBERSHIP_STATUS_LABELS[s]}</option>)}
        </select>
        <select aria-label="State or province" value={stateCode} onChange={(e) => setStateCode(e.currentTarget.value)}
          title={stateRules.error ? `Couldn't load the state list: ${stateRules.error}` : undefined}>
          <option value="">Any state or province</option>
          {stateCode && !stateGroups.some((g) => g.options.some((o) => o.value === stateCode)) ? <option value={stateCode}>{stateCode}</option> : null}
          {stateGroups.map((g) => <optgroup label={g.group}>{g.options.map((o) => <option value={o.value}>{o.label}</option>)}</optgroup>)}
        </select>
        {loading ? <Spinner /> : <span class="muted" style="font-size:13px">{total} found</span>}
      </div>
      {pickedRule?.handPickOnly && pickedRule.callingAllowed ? (
        <p class="vd-handpick" role="note">
          The dialer never picks {pickedRule.name} leads for you. Press <b>Call</b> on each one to open it on the dial screen; it dials only when you press Space.
        </p>
      ) : null}
      <ErrorBanner error={error} />
      {!loading && !error && rows.length === 0 ? <Empty>No leads match.</Empty> : null}
      {rows.length ? (
        <div class="vd-table-wrap">
          <table>
            <thead><tr><th>Business</th><th>Phone</th><th>Where</th><th>Campaigns (attempts)</th><th>Last attempt</th><th></th></tr></thead>
            <tbody>
              {rows.map(({ business: b, memberships }) => {
                const last = memberships.map((m) => m.lastAttemptAt).filter((x): x is string => !!x).sort().pop() ?? null;
                const callable = callableMemberships({ business: b, memberships }, campaignId).map((m) => m.id);
                const byHand = handPickRule(stateRules.data, b);
                return (
                  <tr class="vd-click" onClick={() => setOpenId(b.id)} tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') setOpenId(b.id); }}>
                    <td>
                      <b>{b.name}</b>{b.dmName ? <span class="muted"> · {b.dmName}</span> : null}
                      {flags(b).length ? <div>{flags(b).map((f) => <span class="chip chip-bad" style="margin-right:4px">{f}</span>)}</div> : null}
                    </td>
                    <td class="mono" style="white-space:nowrap">{fmtPhone(b.phoneE164)}</td>
                    <td>
                      {[b.city, b.stateProv].filter(Boolean).join(', ')}
                      {byHand ? <div><span class="chip chip-warn" title={`The dialer never queues ${byHand.name} automatically; open each lead yourself.`}>open by hand</span></div> : null}
                    </td>
                    <td>{memberships.map((m) => <span class={`chip chip-${membershipTone(m.status) || 'x'}`} style="margin:0 4px 4px 0" title={m.campaignName}>{m.campaignName}: {MEMBERSHIP_STATUS_LABELS[m.status] ?? m.status} ({m.attempts})</span>)}</td>
                    <td>{last ? timeAgo(last) : '—'}</td>
                    <td style="text-align:right">
                      {callable.length ? (
                        <button class="btn btn-sm btn-primary" disabled={calling !== null}
                          title={callable.length === 1 ? 'Open it on the dial screen (it will not dial until you press Space)' : 'In more than one open campaign: pick the campaign'}
                          onClick={(e) => { e.stopPropagation(); void callRow(b.id, callable); }}
                          onKeyDown={(e) => e.stopPropagation()}>
                          {calling !== null && callable.includes(calling) ? 'Opening…' : callable.length === 1 ? 'Call' : 'Call…'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {rows.length < total ? <div style="margin-top:10px"><button class="btn" disabled={loading} onClick={() => void load(rows.length)}>Load more</button></div> : null}
      {openId !== null ? <LeadDrawer businessId={openId} onClose={() => setOpenId(null)} onChanged={onChanged} /> : null}
    </div>
  );
}
